
import { computed, defineComponent, ref } from "vue";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { MenuModel } from "../../router";
import Notificacao from "../notificacao/Notificacao.vue";
import Painel from "../painel/Painel.vue";

export default defineComponent({
  setup() {
    const { user } = useAuth();
    const initials = computed(
      () =>
        user?.value &&
        [user.value.userName?.substr(0, 1)].filter((e) => !!e).join("")
    );

    const logoUrl = computed(() => {
      return user?.value && user.value.logoUrl;
    });

    // Usuário logado ao lado do sino: facilita os testes e identifica o usuário nos prints que
    // os clientes enviam. O login é único; o cliente ajuda quando a GovPass opera vários.
    const usuarioLogado = computed(() => {
      const login = user?.value?.userName?.toLowerCase() || "";
      const cliente = user?.value?.nomeCliente || "";
      const nome = user?.value?.nome || "";
      return {
        login,
        cliente,
        titulo: [
          nome && nome.toLowerCase() !== login ? nome : "",
          login,
          cliente
        ]
          .filter((e) => !!e)
          .join(" · ")
      };
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => {
      return useAuth().amIAllowed(authority, permissionInfo);
    };

    const menus = [] as Array<MenuModel>;
    if (
      amIAllowed("view", "root-dashboard") ||
      amIAllowed("view", "root-cliente")
    ) {
      const menuRoot = {
        label: "Root",
        icon: "fas fa-brain",
        items: [] as Array<MenuModel>
      };
      if (amIAllowed("view", "root-dashboard")) {
        menuRoot.items.push({
          label: "Dashboard",
          icon: "fas fa-tachometer-alt",
          to: { name: "root-dashboard" }
        });
        menuRoot.items.push({
          label: "Robôs & Filas",
          icon: "fas fa-robot",
          to: { name: "root-robos-filas" }
        });
      }
      if (amIAllowed("view", "root-cliente")) {
        menuRoot.items.push({
          label: "Clientes",
          icon: "fas fa-users",
          to: { name: "root-cliente" }
        });
      }
      if (amIAllowed("view", "root-dashboard")) {
        menuRoot.items.push({ separator: true });
        menuRoot.items.push({
          label: "Preços RNTRC",
          icon: "fas fa-tags",
          to: { name: "root-rntrc-preco" }
        });
        menuRoot.items.push({
          label: "Prestação de contas RNTRC",
          icon: "fas fa-file-invoice-dollar",
          to: { name: "root-rntrc-prestacao-contas" }
        });
      }
      menus.push(menuRoot);
    }

    if (
      amIAllowed("view", "licenca") ||
      amIAllowed("view", "licenca-combinacao-veiculos") ||
      amIAllowed("view", "relatorio-faturamento")
    ) {
      const menuLicenca = {
        label: "Licenças",
        icon: "fas fa-file-alt",
        items: [] as Array<MenuModel>
      };
      if (amIAllowed("view", "licenca")) {
        menuLicenca.items.push({
          label: "Licenças",
          icon: "fas fa-file-alt",
          to: { name: "licenca" }
        });
      }
      // sem o submenu "Consultas" (15/10/2026): Licenças, Faturamento e Combinação no mesmo nível
      if (amIAllowed("view", "relatorio-faturamento")) {
        menuLicenca.items.push({
          label: "Faturamento",
          icon: "far fa-chart-bar",
          to: { name: "relatorio-faturamento" }
        });
      }
      if (amIAllowed("view", "licenca-combinacao-veiculos")) {
        menuLicenca.items.push({
          label: "Combinação de Veículos",
          icon: "fas fa-truck",
          to: { name: "licenca-combinacao-veiculos" }
        });
      }
      menus.push(menuLicenca);
    }

    if (
      amIAllowed("view", "veiculo-trator") ||
      amIAllowed("view", "carreta") ||
      amIAllowed("view", "veiculo-trator-modelo") ||
      amIAllowed("view", "veiculo-importacao-lote")
    ) {
      const menuVeiculo = {
        label: "Veículos",
        icon: "fas fa-truck",
        items: [] as Array<MenuModel>
      };
      if (amIAllowed("view", "veiculo-trator-modelo")) {
        menuVeiculo.items.push({
          label: "Modelos",
          icon: "fas fa-file-invoice",
          to: { name: "veiculo-trator-modelo" }
        });
      }
      if (amIAllowed("view", "veiculo-trator")) {
        menuVeiculo.items.push({
          label: "Veículos Tratores",
          icon: "fas fa-truck",
          to: { name: "veiculo-trator" }
        });
      }

      if (amIAllowed("view", "carreta")) {
        menuVeiculo.items.push({ separator: true });
        menuVeiculo.items.push({
          label: "Carretas",
          icon: "fas fa-truck-loading",
          to: { name: "carreta" }
        });
      }
      if (amIAllowed("view", "veiculo-importacao-lote")) {
        menuVeiculo.items.push({ separator: true });
        menuVeiculo.items.push({
          label: "Atualização de documentos em lote",
          icon: "fas fa-file-import",
          to: { name: "veiculo-importacao-lote" }
        });
      }

      menus.push(menuVeiculo);
    }
    if (amIAllowed("view", "transportador")) {
      menus.push({
        label: "Transportadores",
        icon: "fas fa-briefcase",
        to: { name: "transportador" }
      });
    }
    if (amIAllowed("view", "engenheiro")) {
      menus.push({
        label: "Engenheiros",
        icon: "fas fa-hard-hat",
        to: { name: "engenheiro" }
      });
    }
    if (amIAllowed("view", "trechos-planilha")) {
      menus.push({
        label: "Planilhas de Trechos",
        icon: "fas fa-road",
        to: { name: "trechos-planilha" }
      });
    }

    if (
      amIAllowed("view", "orcamento") ||
      amIAllowed("view", "tabela-preco") ||
      amIAllowed("view", "contrato-transportador")
    ) {
      const menuOrcamento = {
        label: "Orçamentos",
        icon: "fas fa-dollar-sign",
        items: [] as Array<MenuModel>
      };
      if (amIAllowed("view", "orcamento")) {
        menuOrcamento.items.push({
          label: "Pipeline de Orçamentos",
          icon: "fas fa-columns",
          to: { name: "orcamento" }
        });
      }
      if (
        (amIAllowed("view", "tabela-preco") ||
          amIAllowed("view", "contrato-transportador")) &&
        menuOrcamento.items.length > 0
      ) {
        menuOrcamento.items.push({ separator: true });
      }
      if (amIAllowed("view", "tabela-preco")) {
        menuOrcamento.items.push({
          label: "Tabela de Preço",
          icon: "fas fa-list-ol",
          to: { name: "tabela-preco" }
        });
      }
      if (amIAllowed("view", "contrato-transportador")) {
        menuOrcamento.items.push({
          label: "Contratos com Transportador",
          icon: "fas fa-handshake",
          to: { name: "contrato-transportador" }
        });
      }
      menus.push(menuOrcamento);
    }

    // RNTRC (ANTT): só a listagem e o faturamento; novas solicitações saem da listagem.
    const menuRntrc = {
      label: "RNTRC (ANTT)",
      icon: "fas fa-id-card",
      items: [] as Array<MenuModel>
    };
    if (amIAllowed("view", "rntrc")) {
      menuRntrc.items.push({
        label: "Solicitações",
        icon: "fas fa-list",
        to: { name: "rntrc-solicitacao" }
      });
      menuRntrc.items.push({ separator: true });
      menuRntrc.items.push({
        label: "Faturamento RNTRC",
        icon: "fas fa-file-invoice-dollar",
        to: { name: "rntrc-faturamento" }
      });
      menus.push(menuRntrc);
    }

    const menusUser = [] as Array<MenuModel>;
    if (amIAllowed("view", "admin-cliente")) {
      menusUser.push({
        label: "Configurações",
        icon: "fas fa-user-cog",
        to: { name: "admin-cliente" }
      });
    }
    if (amIAllowed("view", "usuario")) {
      menusUser.push({
        label: "Usuários",
        icon: "fas fa-users",
        to: { name: "usuario" }
      });
    }
    menusUser.push({
      label: "Minha Senha",
      icon: "fas fa-key",
      to: { name: "me-change-password" }
    });
    menusUser.push({
      label: "Sair",
      icon: "pi pi-fw pi-power-off",
      to: { name: "logout" }
    });

    const menusRefs = ref(menus);
    const menusUserRefs = ref(menusUser);
    const menuUser = ref();

    const toggleComponentMenu = (evt: Event) => {
      ((evt.target as HTMLElement)?.firstElementChild as HTMLElement)?.click();
    };
    const toggleUserMenu = (event: Event) => {
      menuUser.value.toggle(event);
    };

    return {
      user,
      initials,
      usuarioLogado,
      menusRefs,
      menusUserRefs,
      menuUser,
      logoUrl,
      toggleComponentMenu,
      toggleUserMenu,
      amIAllowed
    };
  },

  components: {
    Notificacao,
    Painel
  }
});
